import EmptyState from "@/components/EmptyState";
import Icon from "@/components/Icon";
import Table, { buildActionColumn, buildTwoLineColumn } from "@/components/Table";
import Tag from "@/components/Tag";
import Tooltip from "@/components/Tooltip";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import { DOMAIN, PERMISSION, useHasCapabilityBool, useRequireCapability } from "@/hooks/useCapabilities";
import { useNavigate, useParams } from "@/router";
import { useQuery } from "@apollo/client";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { useState } from "react";
import { GET_PAYOUT_PROFILES_QUERY, type GetPayoutProfilesResponse, type PayoutProfile } from "./queries";
import { compareIsoDatesNullsLast, getStatusData, isPersonal, isW8, taxClassificationDisplay } from "./utils";
import BankInstitutionCell from "./_components/BankInstitutionCell";
import TokenizedAccountNumberHint from "./_components/TokenizedAccountNumberHint";

export default function PayoutProfilesPage() {
  useRequireCapability({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPersonBilling });
  const canViewPersonBilling = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonBilling
  });
  const { setMenu, Menu: ActionMenu } = useTableRowMenu();
  const { pgaId } = useParams("/people/:pgaId/billing/payout-profiles");
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([{ id: "profile", desc: false }]);

  const { data, loading } = useQuery<GetPayoutProfilesResponse>(GET_PAYOUT_PROFILES_QUERY, {
    variables: { pgaId },
    skip: !pgaId || !canViewPersonBilling
  });

  const tableData = data?.payoutProfiles?.nodes || [];

  const columns: ColumnDef<PayoutProfile>[] = [
    buildTwoLineColumn<PayoutProfile>({
      accessorKey: "taxFormType",
      header: "Profile",
      getPrimaryText: (row) => (isPersonal(row) ? "Personal" : "Business"),
      getSecondaryText: (row) => (isW8(row) ? "Foreign" : taxClassificationDisplay(row.taxClassification || "--")),
      size: 200,
      minSize: 150,
      left: (row) => {
        if (!row.defaultPayoutProfile) return null;
        return (
          <Tooltip
            variant="plain"
            supportingText="Default Payout Profile"
            color="black"
            menuCorner="end-start"
            anchorCorner="start-start"
          >
            <Icon name="check" size={24} />
          </Tooltip>
        );
      }
    }),
    {
      id: "recipient",
      header: "Recipient",
      enableSorting: true,
      accessorFn: (row) => {
        const businessName = row.businessName;
        const name = row.name;
        return businessName && businessName !== "--" ? businessName : name;
      },
      sortingFn: (rowA, rowB) => {
        const getRecipient = (row: PayoutProfile) => {
          const businessName = row.businessName;
          const name = row.name;
          return businessName && businessName !== "--" ? businessName : name;
        };
        const a = getRecipient(rowA.original) || "";
        const b = getRecipient(rowB.original) || "";
        return a.localeCompare(b);
      },
      cell: ({ row }) => {
        const businessName = row.original.businessName;
        const name = row.original.name;
        // Show business name if available and not empty/"--", otherwise show name
        return businessName && businessName !== "--" ? businessName : name;
      }
    },
    {
      header: "Institution",
      accessorKey: "bankName",
      enableSorting: true,
      cell: ({ row }) => (
        <BankInstitutionCell
          bankName={row.original.bankName}
          bankConnectionProvider={row.original.bankConnectionProvider}
        />
      )
    },
    {
      header: "Account Number",
      accessorKey: "bankAccountMaskedNumber",
      enableSorting: true,
      cell: ({ row }) => (
        <TokenizedAccountNumberHint
          maskedNumber={row.original.bankAccountMaskedNumber}
          identifierType={row.original.stripeFcAccountNumberIdentifierType}
        />
      )
    },
    {
      id: "stripeFcExpectedExpiryDate",
      header: "Bank Expiry",
      accessorKey: "stripeFcExpectedExpiryDate",
      enableSorting: true,
      sortingFn: (rowA, rowB) =>
        compareIsoDatesNullsLast(
          rowA.original.stripeFcExpectedExpiryDate,
          rowB.original.stripeFcExpectedExpiryDate,
          sorting.some((s) => s.id === "stripeFcExpectedExpiryDate" && s.desc)
        ),
      cell: ({ row }) => row.original.stripeFcExpectedExpiryDate || <span className="opacity-20">--</span>
    },
    {
      header: "Status",
      accessorKey: "status",
      enableSorting: true,
      meta: {
        headerClassName: "hide-header-in-column"
      },
      cell: ({ row }) => {
        const statusData = getStatusData(row.original);
        return (
          <div className="flex flex-col gap-1 items-start">
            <Tag tagType={statusData.tagType} size="small">
              {statusData.label}
            </Tag>
            {row.original.bankReauthorizationRequired && (
              <Tag tagType="error" size="small">
                Bank Reauth Needed
              </Tag>
            )}
          </div>
        );
      }
    },
    buildActionColumn<PayoutProfile>({
      getActions: (row) => [
        {
          text: "View Profile",
          onClick: () => navigate(`/people/${pgaId}/billing/payout-profiles/${row.id}/view` as any)
        }
      ],
      actionId,
      setMenu
    })
  ];

  return (
    <>
      <div className="pt-12" />
      <Table
        data-testid="payout-profiles-table"
        data={tableData}
        columns={columns}
        loading={loading}
        getRowLink={(row) => ({
          to: `/people/${pgaId}/billing/payout-profiles/${row.original.id}/view` as any
        })}
        sortingState={sorting}
        onSortingChange={setSorting}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No Payout Profiles"
            caption="This person does not have any payout profiles."
            iconName="account_balance"
          />
        )}
      />
      <ActionMenu />
    </>
  );
}
